import React, { useState } from 'react';
import { formatCurrencySigFig } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { useFormContextOptional } from '../../contexts/FormContext';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import InsightColumnPair from '../InsightColumnPair';
import './MissedEconomicImpact.css';

interface InsightData {
  missedEconomyValue?: number;
  missedEconomyUncertainty?: number;
}

interface MissedEconomicImpactData {
  insights?: InsightData;
  originalInsights?: InsightData;
  parametersChanged?: boolean;
  stockName?: string;
  onOpenEconomicImpactModal?: () => void;
}

function getDescription(stockName?: string): string {
  const base =
    ', calculated as the missed ex-vessel value × multiplier (currently ≈ 4.6)';
  if (!stockName || stockName === 'All Stocks') {
    return `The total economic contribution that could have been generated by the fish that were not caught from all stocks${base}`;
  }
  return `The total economic contribution that could have been generated by the fish that were not caught from ${stockName}${base}`;
}

export default function MissedEconomicImpact(
  props: RegistryComponentProps
): React.ReactElement {
  const formContext = useFormContextOptional();
  const pageData = usePageDataContextOptional();
  const injectionData = getRegistryData<MissedEconomicImpactData>(props);

  const [expanded, setExpanded] = useState(false);

  const pageInsights = pageData?.data?.insights as InsightData | undefined;
  const pageOriginalInsights = pageData?.data?.originalInsights as InsightData | undefined;
  const pageLoading = pageData?.loading as boolean | undefined;
  const pageError = pageData?.error as string | null | undefined;

  const pageOpenEconomicImpactModal = pageData?.actions?.openEconomicImpactModal as (() => void) | undefined;
  const pageStockName = pageData?.data?.stockName as string | undefined;
  const insights = injectionData.insights ?? pageInsights ?? {};
  const originalInsights = injectionData.originalInsights ?? pageOriginalInsights;
  const parametersChanged = (injectionData.parametersChanged ?? pageData?.data?.parametersChanged) ?? false;
  const stockName = injectionData.stockName ?? pageStockName;
  const onOpenEconomicImpactModal = injectionData.onOpenEconomicImpactModal ?? pageOpenEconomicImpactModal;

  const selectedYear = (formContext?.values?.['time-period'] as string) ?? 'projections';
  const showSingleColumn = selectedYear === 'historical';
  const showStatusQuoColumn = !showSingleColumn;
  const showChangeIndicators =
    !showSingleColumn && !!originalInsights && parametersChanged;

  const statusQuoDisplay = originalInsights ?? insights;
  const description = getDescription(stockName);

  return (
    <div className="missed-economic-impact insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Missed Economic Impact</h3>
        <button
          className="info-icon"
          onClick={() => setExpanded((e) => !e)}
          aria-label="Toggle description"
          type="button"
        >
          i
        </button>
      </div>
      <div className={`insight-subtitle-container ${expanded ? 'expanded' : ''}`}>
        <h2 className="insight-subtitle" dangerouslySetInnerHTML={{ __html: description }} />
      </div>
      <InsightColumnPair
        originalValue={originalInsights?.missedEconomyValue}
        currentValue={insights.missedEconomyValue}
        statusQuoContent={
          <>
            {formatCurrencySigFig(statusQuoDisplay.missedEconomyValue ?? 0)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(statusQuoDisplay.missedEconomyUncertainty ?? 0, false)})
            </div>
          </>
        }
        currentContent={
          <>
            {formatCurrencySigFig(insights.missedEconomyValue ?? 0)}
            <div className="uncertainty-value">
              (± {formatCurrencySigFig(insights.missedEconomyUncertainty ?? 0, false)})
            </div>
          </>
        }
        showStatusQuoColumn={showStatusQuoColumn}
        showSingleColumn={showSingleColumn}
        showChangeIndicators={Boolean(showChangeIndicators)}
        parametersChanged={Boolean(parametersChanged)}
        onClick={onOpenEconomicImpactModal}
        clickable={Boolean(onOpenEconomicImpactModal)}
        isLoading={Boolean(pageLoading)}
        errorMessage={pageError}
      />
    </div>
  );
}
